import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { adminLoginAs } from '~/acl';
import { impersonate, search } from '~/admin/api';
import { useAuthData } from '~/auth';
import { isImpersonating, restoreAdminToken, storeImpersonatedToken } from '~/auth/token';
import { AvatarUser } from '~/components/new/Avatar';
import List from '~/components/new/List';
import { Menu, MenuRowCustom, MenuRowLink } from '~/components/new/Menu';
import { Select } from '~/components/ui/Form';
import { SelectOption } from '~/components/ui/forms/Select.tsx';
import { useOnClickOutside } from '~/components/ui/hooks';
import { RemixIconNames } from '~/components/ui/Icon';
import { User } from '~/types/domain.ts';
import { isProduction } from '~/utils/env';
import { classNames } from '~/utils/styles';

type ClickLikeEvent = {
    preventDefault: () => void;
    stopPropagation: () => void;
};

function restoreAdminFromMenu(e: ClickLikeEvent) {
    e.preventDefault();
    e.stopPropagation();
    restoreAdminToken();
    window.location.pathname = '/';
}

export default function UserMenu() {
    const { user } = useAuthData();
    const ref = useRef<HTMLDivElement>(null!);
    const [open, setOpen] = useState(false);
    useOnClickOutside(ref, () => setOpen(false));
    const navigate = useNavigate();

    if (!user.authenticated) return null;

    return (
        <div
            ref={ref}
            onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
            }}>
            <AvatarUser
                className='cursor-pointer'
                user={user}
                variant='base'
            />
            {open && (
                <Menu
                    className='topbar-user-menu'
                    typology='custom'>
                    <List>
                        <List.Row
                            icon={
                                <AvatarUser
                                    user={user}
                                    variant='base'
                                />
                            }
                            title={user.fullName}
                            subtitle={user.company}
                        />
                    </List>

                    <ImpersonateForm />
                    {(user.isContractor() || user.isCandidate() || user.isCustomer()) && (
                        <MenuRowLink
                            label='Profile'
                            onClick={() => {
                                void navigate('/profile');
                            }}
                            icon={RemixIconNames.RiUserLine}
                        />
                    )}
                    {(!isProduction() || user.isAdmin()) && (
                        <MenuRowLink
                            label='Developer tools'
                            onClick={() => {
                                void navigate('/dev');
                            }}
                            icon={RemixIconNames.RiCodeBoxLine}
                        />
                    )}
                    <MenuRowLink
                        label='Settings'
                        onClick={() => {
                            void navigate('/settings');
                        }}
                        icon={RemixIconNames.RiSettingsLine}
                    />
                    <MenuRowLink
                        label='Logout'
                        onClick={() => {
                            void navigate('/logout');
                        }}
                        icon={RemixIconNames.RiLogoutBoxRLine}
                    />
                </Menu>
            )}
        </div>
    );
}

export function ImpersonateForm({
    menuPlacement = 'bottom',
    className = '',
}: {
    menuPlacement?: string;
    className?: string;
}) {
    const { user } = useAuthData();
    const [loading, setLoading] = useState(false);

    if (!user.hasAccess(adminLoginAs) && !isImpersonating()) {
        return null;
    }

    const asyncLoadOptions = async (inputValue: string) => {
        const data = await search<User>('users', inputValue);
        return data.map((r) => ({
            label: `${r.first_name} ${r.last_name} (${r.email})`,
            value: r.id!,
        }));
    };

    const onChange = (v: SelectOption<number>) => {
        setLoading(true);

        impersonate(v.value)
            .then((token) => {
                storeImpersonatedToken(token.token, token.expires_at);
                window.location.pathname = '/';
            })
            .catch(() => {
                setLoading(false);
            });
    };

    return (
        <div
            className={classNames({
                'topbar-user-menu-impersonate': true,
                [className]: Boolean(className),
            })}>
            {user.hasAccess(adminLoginAs) && (
                <MenuRowCustom
                    onClick={(e) => e.stopPropagation()}
                    className='topbar-user-menu-impersonate-row'>
                    <Select
                        cacheOptions
                        disabled={loading}
                        placeholder='Login as...'
                        loadOptions={asyncLoadOptions}
                        onChange={onChange}
                        menuPlacement={menuPlacement}
                        autoFocus
                    />
                </MenuRowCustom>
            )}
            {isImpersonating() && (
                <MenuRowLink
                    label='Log back as admin'
                    onClick={restoreAdminFromMenu}
                    icon={RemixIconNames.RiLogoutBoxRLine}
                />
            )}
        </div>
    );
}
